<template>
  <ProductDetail
    v-if="isProductDetailPage"
    :forced-slug="slug"
    :key="`detail-${slug}`"
  />
  <ProductListing
    v-else-if="isProductCategoryPage"
    :forced-category-slug="slug"
    :category-path-builder="productCategoryPathBuilder"
    :base-products-path-override="baseProductsPath"
    :key="`listing-${slug}`"
  />
  <div v-else-if="pageData?.[0]" class="w-full space-y-12 lg:space-y-20">
    <SectionsHeroDetail
      v-if="pageData?.[0]?.title"
      :key="'heroDetail'"
      :title="pageData?.[0]?.title"
      :description="pageData?.[0]?.description"
      :media="pageData?.[0]?.media"
      :mask="pageData?.[0]?.mask"
      :backgroundColor="pageData?.[0]?.backgroundColor"
      :color="pageData?.[0]?.color"
      :breadcrumbs="breadcrumbs"
    />
    <SectionsSectionRenderer
      :sections="pageData?.[0]?.sections"
      :animate="animateOnScroll"
    />
  </div>
</template>
<script setup>
import { computed } from "vue";
import { useApi } from "~/server/getApi";
import qs from "qs";
import { useI18n } from "vue-i18n";
import { useSiteSettingsStore } from "@/store/index";
import { useMetaTags } from "~/composables/headConfig";
import {
  buildJsonLdScripts,
  createFaqSchema,
  createWebPageSchema,
  createBreadcrumbSchema,
} from "~/composables/schemaOrg";
import ProductDetail from "~/components/products/ProductDetail.vue";
import ProductListing from "~/components/products/ProductListing.vue";
import { useLocalePath } from "#i18n";
const { t, locale } = useI18n();

const route = useRoute();
const config = useRuntimeConfig();

const baseUrl = config.public.baseUrl;
const siteUrl = config.public.siteUrl;
const fullUrl = computed(() => `${siteUrl}${route.fullPath}`);

const slug = computed(() => route.params.slug || "fallback-degeri");
const localePath = useLocalePath();
const siteSettingsStore = useSiteSettingsStore();
const animateOnScroll = computed(() =>
  Boolean(siteSettingsStore.animateScroll),
);
const baseProductsPath = computed(
  () => localePath({ name: "products" }) || "/products",
);
const productCategoryPathBuilder = (categorySlug) => {
  if (!categorySlug) {
    return baseProductsPath.value;
  }
  const prefix = locale.value === "tr" ? "" : `/${locale.value}`;
  return `${prefix}/${encodeURIComponent(categorySlug)}`;
};

const extractSlugFromEntry = (entry) => {
  if (!entry) {
    return null;
  }
  const normalized = entry?.attributes ?? entry;
  return typeof normalized?.slug === "string" ? normalized?.slug : null;
};

const productCategoryCheckQuery = computed(() =>
  qs.stringify(
    {
      filters: {
        slug: {
          $eq: slug.value,
        },
      },
      fields: ["slug"],
      pagination: {
        pageSize: 1,
      },
    },
    { encodeValuesOnly: true },
  ),
);

const { data: productCategoryCheckData, fetchData: fetchProductCategoryCheck } =
  useApi(`product-categories?${productCategoryCheckQuery.value}`, true);

const productDetailQuery = computed(() =>
  qs.stringify(
    {
      filters: {
        slug: {
          $eq: slug.value,
        },
      },
      fields: ["slug"],
      pagination: {
        pageSize: 1,
      },
    },
    { encodeValuesOnly: true },
  ),
);

const { data: productDetailData, fetchData: fetchProductDetailCheck } = useApi(
  `product-lists?${productDetailQuery.value}`,
  true,
);

// SSR needs these awaited to suspend the component until data is ready
await Promise.all([fetchProductCategoryCheck(), fetchProductDetailCheck()]);

const detectedCategorySlug = computed(() => {
  const raw = productCategoryCheckData.value;
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : [];
  return extractSlugFromEntry(list[0]);
});

const detectedProductSlug = computed(() => {
  const raw = productDetailData.value;
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : [];
  return extractSlugFromEntry(list[0]);
});

const isProductCategoryPage = computed(() =>
  Boolean(detectedCategorySlug.value),
);
const isProductDetailPage = computed(() => Boolean(detectedProductSlug.value));

const query = computed(() => {
  return qs.stringify(
    {
      filters: {
        slug: {
          $eq: slug.value,
        },
      },
    },
    { encodeValuesOnly: true },
  );
});

const {
  data: pageData,
  error: pageError,
  fetchData: fetchPageData,
  triggerError,
} = useApi(`pages?${query.value}&pLevel`, false, { immediate: false });

const breadcrumbs = computed(() => [
  {
    enabled: true,
    link: locale.value === "tr" ? "/" : `/${locale.value}`,
    title: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 md:w-5 md:h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955a1.125 1.125 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>`,
  },
  {
    enabled: true,
    link: `/${slug.value}`,
    title: pageData.value?.[0]?.title,
  },
]);

if (!isProductCategoryPage.value && !isProductDetailPage.value) {
  await fetchPageData();
  
  // Explicitly trigger 404 if no page content is found
  if (import.meta.server && (!pageData.value || pageData.value.length === 0)) {
    triggerError(404, "Sayfa bulunamadı");
  }
}

/* -------------------- 🔧 SEO Etiketi -------------------- */
const seoData = computed(() => {
  if (isProductCategoryPage.value || isProductDetailPage.value) return {};
  return pageData.value?.[0]?.seo ?? {};
});
const pageTitle = computed(() => {
  if (isProductCategoryPage.value || isProductDetailPage.value) return "";
  return pageData.value?.[0]?.title;
});
const pageDescription = computed(() => {
  if (isProductCategoryPage.value || isProductDetailPage.value) return "";
  return pageData.value?.[0]?.description;
});

useMetaTags(seoData, pageTitle, pageDescription);

watch(
  () => slug.value,
  async () => {
    // Refresh the check queries — useAsyncData handles the actual new fetch
    await Promise.all([fetchProductCategoryCheck(true), fetchProductDetailCheck(true)]);
    if (!isProductCategoryPage.value && !isProductDetailPage.value) {
      await fetchPageData(true);
    }
  },
);

// watch(slug, async () => {
//   await fetchPageData();
// });

/* -------------------- 📄 UseHead Schema for Page -------------------- */
useHead(() => {
  if (isProductCategoryPage.value || isProductDetailPage.value) {
    return { script: [] };
  }
  const page = pageData.value?.[0];
  if (!page) {
    return { script: [] };
  }

  const faqSection = page.sections?.find(
    (section) =>
      ["sections.accordion", "sections.image-accordion"].includes(
        section.__component,
      ) && section.enabled,
  );

  const scripts = buildJsonLdScripts([
    {
      key: "schema-generic-page",
      schema: createWebPageSchema({
        type: "WebPage",
        url: fullUrl.value,
        name: page.title,
        headline: page.title,
        description: page.seo?.description || page.description,
        publishedAt: page.publishedAt,
        updatedAt: page.updatedAt,
        keywords: page.seo?.keywords,
        baseUrl,
      }),
    },
    {
      key: "schema-generic-page-faq",
      schema: createFaqSchema(faqSection?.items),
    },
    {
      key: "schema-generic-page-breadcrumbs",
      schema: createBreadcrumbSchema(breadcrumbs.value, siteUrl),
    },
  ]);

  return {
    script: scripts,
  };
});
</script>
